import { useNavigate } from "react-router-dom";

import { useHealth, useRunValidation } from "@/api/queries";
import { Button } from "@/components/Button";
import { ProjectSwitcher } from "@/components/ProjectSwitcher";
import { Tag } from "@/components/Tag";
import { useUiStore } from "@/store/ui";
import { metaFor } from "@/views/meta";

/**
 * 工作台顶栏（原型 `.topbar`，72px）。
 *
 * 数据来源：`@dataSource mixed` —— 标题取自路由元信息（静态），
 * 项目切换与后端连通状态来自真实接口（`/projects`、`/health`）。
 */
export function Topbar({ view }: { view: string }) {
  const navigate = useNavigate();
  const projectId = useUiStore((state) => state.projectId);
  const pushToast = useUiStore((state) => state.pushToast);
  const runValidation = useRunValidation(projectId);
  const health = useHealth();
  const meta = metaFor(view);

  return (
    <header className="topbar">
      <div className="topbar-title">
        <span id="view-eyebrow">{meta.eyebrow}</span>
        <h1 id="view-title">{meta.title}</h1>
      </div>

      <ProjectSwitcher />

      {health.isError ? <Tag tone="red">后端未连接</Tag> : null}

      <div className="topbar-actions">
        <Button
          id="global-check"
          variant="quiet"
          disabled={!projectId || runValidation.isPending}
          onClick={() => {
            runValidation.mutate(undefined, {
              onSuccess: (result) => {
                const blocking = Number(result.summary.blocking ?? 0);
                const total = Number(result.summary.total ?? result.issues.length);
                pushToast({
                  title: blocking > 0 ? `${blocking} 项阻塞` : "检查通过",
                  detail:
                    blocking > 0 ? "阻塞项须先修复，下游产物不得标记正式" : `共 ${total} 项问题`,
                  mark: blocking > 0 ? "!" : "✓",
                });
              },
              onError: (error) => {
                pushToast({
                  title: "检查失败",
                  detail: error instanceof Error ? error.message : "未知错误",
                  mark: "!",
                });
              },
            });
          }}
        >
          <span aria-hidden="true">✓</span>
          <span>{runValidation.isPending ? "检查中…" : "运行检查"}</span>
        </Button>
        <Button id="global-export" variant="primary" onClick={() => navigate("/studio/export")}>
          <span aria-hidden="true">↥</span>
          <span>导出交付</span>
        </Button>
      </div>
    </header>
  );
}
